import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'
import { voidInvoice } from '@/api/invoices'
import { getShipmentById, removeShipment, returnShipmentByCustomer, writeOffShipment } from '@/api/shipments'
import type { Invoice, ShipmentWithCustomer } from '@/api/types'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ShipmentBillingFields } from '@/components/shipment/ShipmentBillingFields'
import { ShipmentInvoiceSummary } from '@/components/shipment/ShipmentInvoiceSummary'
import { getApiErrorMessage } from '@/lib/apiError'
import { hasPermission } from '@/lib/permissions'
import { formatCustomerNumber } from '@/lib/customer'
import {
  canMarkReturnedByCustomer,
  canRemoveShipment,
  canChangeShipmentBilling,
  canWriteOffShipment,
  formatShipmentNumber,
  getCustomerReturnConfirmMessage,
  getRemoveShipmentConfirmMessage,
  getWriteOffShipmentConfirmMessage,
  isReturnedByCustomer,
  SHIPMENT_STATUS_LABELS
} from '@/lib/shipment'
import { returnShipmentWithInvoicePrompt, writeOffShipmentWithInvoicePrompt } from '@/lib/shipmentWriteOff'
import type { WriteOffOutcome } from '@/lib/shipmentWriteOff'

const REMOVE_ERROR_MESSAGE = 'Kunde inte ta bort leveransen just nu.'
const WRITE_OFF_ERROR_MESSAGE = 'Kunde inte skriva av leveransen just nu.'
const CUSTOMER_RETURN_ERROR_MESSAGE = 'Kunde inte markera leveransen som returnerad just nu.'

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString('sv-SE') : '-'
}

export function ShipmentDetailPage(): React.ReactElement {
  const { shipmentId } = useParams()
  const [shipment, setShipment] = useState<ShipmentWithCustomer | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const [removeErrorMessage, setRemoveErrorMessage] = useState<string | null>(null)
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null)
  const [invoiceVersion, setInvoiceVersion] = useState(0)
  const [shipmentInvoice, setShipmentInvoice] = useState<Invoice | null | undefined>(undefined)
  const user = useAuthStore((state) => state.user)

  const canManage = hasPermission(user, 'subscriptions.manage')
  const canVoidInvoices = hasPermission(user, 'invoicing.void')
  const canRemove = shipment !== null && canRemoveShipment(shipment) && canManage
  const canWriteOff = shipment !== null && canWriteOffShipment(shipment) && canManage
  const canMarkReturned = shipment !== null && canMarkReturnedByCustomer(shipment) && canManage
  const isCustomerReturn = shipment !== null && isReturnedByCustomer(shipment)
  const isInvoiceLoaded = shipmentInvoice !== undefined
  const canChangeBilling = shipment !== null && isInvoiceLoaded && canChangeShipmentBilling(shipment, shipmentInvoice !== null) && canManage

  useEffect(() => {
    const id = Number(shipmentId)

    if (!Number.isFinite(id)) {
      setErrorMessage('Ogiltigt leverans-id.')
      return
    }

    const loadShipment = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        setShipment(await getShipmentById(id))
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          setErrorMessage('Leveransen hittades inte.')
          return
        }

        setErrorMessage('Kunde inte hämta leveransen just nu.')
      } finally {
        setIsLoading(false)
      }
    }

    void loadShipment()
  }, [shipmentId])

  const handleRemove = async (): Promise<void> => {
    if (!shipment || !window.confirm(getRemoveShipmentConfirmMessage(shipment))) {
      return
    }

    setIsRemoving(true)
    setRemoveErrorMessage(null)

    try {
      setShipment(await removeShipment(shipment.id))
    } catch (error) {
      setRemoveErrorMessage(getApiErrorMessage(error, REMOVE_ERROR_MESSAGE))
    } finally {
      setIsRemoving(false)
    }
  }

  const runInvoicePromptAction = async (request: () => Promise<WriteOffOutcome>, failureMessage: string): Promise<void> => {
    setIsRemoving(true)
    setRemoveErrorMessage(null)
    setNoticeMessage(null)

    try {
      const outcome = await request()

      setShipment(outcome.shipment)
      setNoticeMessage(outcome.notice)
      setInvoiceVersion((version) => version + 1)
    } catch (error) {
      setRemoveErrorMessage(getApiErrorMessage(error, failureMessage))
    } finally {
      setIsRemoving(false)
    }
  }

  const handleWriteOff = async (): Promise<void> => {
    if (!shipment || !window.confirm(getWriteOffShipmentConfirmMessage(shipment))) {
      return
    }

    const dependencies = { writeOff: writeOffShipment, voidInvoice, confirm: (message: string) => window.confirm(message) }

    await runInvoicePromptAction(
      () => writeOffShipmentWithInvoicePrompt(shipment.id, canVoidInvoices, dependencies),
      WRITE_OFF_ERROR_MESSAGE
    )
  }

  const handleCustomerReturn = async (): Promise<void> => {
    if (!shipment || !window.confirm(getCustomerReturnConfirmMessage(shipment))) {
      return
    }

    const dependencies = { returnShipment: returnShipmentByCustomer, voidInvoice, confirm: (message: string) => window.confirm(message) }

    await runInvoicePromptAction(
      () => returnShipmentWithInvoicePrompt(shipment.id, canVoidInvoices, dependencies),
      CUSTOMER_RETURN_ERROR_MESSAGE
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-serif text-primary">Leverans</h1>
        <Button
          asChild
          variant="outline"
          className="rounded-none"
        >
          <Link to="/deliveries">Tillbaka</Link>
        </Button>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Laddar leverans...</p>}

      {errorMessage && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {!isLoading && shipment && (
        <Card className="shadow-none border-border/80">
          <CardHeader>
            <CardTitle>Leverans #{shipment.sequence}</CardTitle>
            <CardDescription>
              {formatShipmentNumber(shipment.id)} · {SHIPMENT_STATUS_LABELS[shipment.status]}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-sm text-muted-foreground">Kund</p>
                <Link
                  to={`/customers/${shipment.customer_id}`}
                  className="underline"
                >
                  {shipment.customer_name ?? 'Okänd kund'}
                </Link>
                <p className="text-sm text-muted-foreground">{formatCustomerNumber(shipment.customer_id)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Produkter</p>
                <p>{shipment.products.map((product) => `${product.quantity}× ${product.name}`).join(', ') || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Spårningsnummer</p>
                <p>{shipment.postnord_shipment_id ?? 'Inte bokad ännu'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Bokad</p>
                <p>{formatDate(shipment.booked_at)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Levererad</p>
                <p>{shipment.delivered_at ? formatDate(shipment.delivered_at) : 'Inte levererad ännu'}</p>
              </div>
              {isCustomerReturn && (
                <div>
                  <p className="text-sm text-muted-foreground">Returnerad av kunden</p>
                  <p>{formatDate(shipment.returned_by_customer_at)}</p>
                </div>
              )}
              <ShipmentInvoiceSummary
                key={invoiceVersion}
                shipmentId={shipment.id}
                customerId={shipment.customer_id}
                onInvoiceLoaded={setShipmentInvoice}
              />
              {canChangeBilling && (
                <ShipmentBillingFields
                  shipment={shipment}
                  onSaved={setShipment}
                />
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              {shipment.label_url ? (
                <Button
                  asChild
                  variant="outline"
                  className="rounded-none"
                >
                  <a
                    href={shipment.label_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Fraktsedel
                  </a>
                </Button>
              ) : (
                <Button
                  variant="outline"
                  className="rounded-none"
                  disabled
                >
                  Fraktsedel saknas
                </Button>
              )}

              {shipment.tracking_url && (
                <Button
                  asChild
                  variant="outline"
                  className="rounded-none"
                >
                  <a
                    href={shipment.tracking_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Spåra
                  </a>
                </Button>
              )}

              {canRemove && (
                <Button
                  variant="destructive"
                  className="rounded-none"
                  disabled={isRemoving}
                  onClick={handleRemove}
                >
                  Ta bort leverans
                </Button>
              )}

              {canWriteOff && (
                <Button
                  variant="destructive"
                  className="rounded-none"
                  disabled={isRemoving}
                  onClick={handleWriteOff}
                >
                  Skriv av leverans
                </Button>
              )}

              {canMarkReturned && (
                <Button
                  variant="destructive"
                  className="rounded-none"
                  disabled={isRemoving}
                  onClick={handleCustomerReturn}
                >
                  Returnerad av kunden
                </Button>
              )}
            </div>

            {noticeMessage && <p className="text-sm text-muted-foreground">{noticeMessage}</p>}

            {removeErrorMessage && <p className="text-sm text-destructive">{removeErrorMessage}</p>}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
